import { CONTENT_TYPES } from '@federa/const/http-headers';
import { CONTENT_TYPES_UTF8, withUtf8Charset } from '@federa/const/media-types';
import { BYTES_PER_MB } from '@federa/const/operations';

/** Maximum file size for chat attachments (10 MB) */
export const CHAT_ATTACHMENT_MAX_SIZE_BYTES = 10 * BYTES_PER_MB;

/** Maximum file size for action step attachments (25 MB) */
export const ACTION_ATTACHMENT_MAX_SIZE_BYTES = 25 * BYTES_PER_MB;

/** Maximum body size for a remotely-fetched image before buffering into Worker heap (25 MB). Guards processImageResponse against oversized external images exhausting the Worker memory limit. */
export const MAX_IMAGE_DOWNLOAD_BYTES = 25 * BYTES_PER_MB;

/** Cloudflare R2's own minimum multipart PART size. Module-private: the only decision a consumer makes is the chunk
 *  size it uploads with, and that is {@link R2_DEFAULT_CHUNK_SIZE_BYTES} -- which IS this floor, so exporting both
 *  offered two names for one number. The floor is pinned through the exported default in `const/__tests__/files.test.ts`. */
const R2_MIN_CHUNK_SIZE_BYTES: number = 5 * BYTES_PER_MB;
/** R2's per-object ceiling, 5 TiB. `BYTES_PER_MB * BYTES_PER_MB` is 1024^4 = one TiB. */
export const R2_MAX_FILE_SIZE_BYTES: number = 5 * BYTES_PER_MB * BYTES_PER_MB;
export const R2_MAX_PARTS: number = 10000;
/** The chunk size every multipart upload uses: R2's minimum part size, so a part is never rejected as too small. */
export const R2_DEFAULT_CHUNK_SIZE_BYTES: number = R2_MIN_CHUNK_SIZE_BYTES;
export const R2_MULTIPART_THRESHOLD_BYTES: number = 20 * BYTES_PER_MB;

/** Per-field byte budget for R2 customMetadata values. R2's total-cap is 2 KiB; this is the conservative per-field limit. */
export const R2_METADATA_VALUE_MAX_BYTES = 512;

/** RFC 2397 data-URL scheme prefix. Use with `String#startsWith` to detect inline data URLs. */
export const DATA_URL_PREFIX = 'data:' as const;

export const EXT_MIME = {
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
	png: CONTENT_TYPES.IMAGE_PNG,
	webp: 'image/webp',
	gif: 'image/gif',
	svg: 'image/svg+xml',
	heic: 'image/heic',
	heif: 'image/heif',
	avif: 'image/avif',
	mp4: 'video/mp4',
	webm: 'video/webm',
	mov: 'video/quicktime',
	mkv: 'video/x-matroska',
	mp3: 'audio/mpeg',
	wav: 'audio/wav',
	ogg: 'audio/ogg',
	flac: 'audio/flac',
	pdf: CONTENT_TYPES.APPLICATION_PDF,
	txt: CONTENT_TYPES_UTF8.TEXT_PLAIN,
	csv: withUtf8Charset('text/csv'),
	html: CONTENT_TYPES_UTF8.TEXT_HTML,
	css: withUtf8Charset('text/css'),
	js: 'application/javascript',
	json: CONTENT_TYPES_UTF8.JSON,
	xml: CONTENT_TYPES.XML,
	zip: 'application/zip',
	gz: 'application/gzip',
	tar: 'application/x-tar',
	doc: 'application/msword',
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	xls: 'application/vnd.ms-excel',
	xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	ppt: 'application/vnd.ms-powerpoint',
	pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
} as const;

/**
 * Media type for a bare file extension, or `fallback` when the extension is not in {@link EXT_MIME}.
 *
 * The named cure for template-composed media types (`` `image/${ext}` ``), which FABRICATE types no registry
 * carries the moment the extension is not already the subtype: `svg` composes to `image/svg` where the canonical
 * value is `image/svg+xml`, and `pdf` composes to `image/pdf` where it is `application/pdf`. Indexing `EXT_MIME`
 * directly needs a cast at every call site because its keys are a closed literal set, so the lookup lives here once.
 * Paired forbid-rule: `QUAL-MEDIA-TYPE-TEMPLATE-COMPOSED-001`.
 *
 * @runtime-derived-ssot-helper (ssot-derived): the read side of the same-file {@link EXT_MIME} table -- it is the cast
 * the closed key set would otherwise force on every caller, and {@link MIME_TO_EXT} is the inverse built from that
 * same table, so both directions of the mapping stay with the mapping.
 */
export function mediaTypeForExtension(ext: string, fallback: string): string {
	const table: Record<string, string> = EXT_MIME;
	const key = ext.toLowerCase();
	// OWN keys only: the extension is caller-supplied text, and a bare index of `constructor` or `__proto__` answers an
	// inherited Object member (a function, the prototype) that `??` does not treat as absent.
	return Object.hasOwn(table, key) ? (table[key] ?? fallback) : fallback;
}

/** Inverse mapping: base MIME type to preferred file extension, built from EXT_MIME. When multiple extensions map to the same MIME type (jpg and jpeg), the first match wins. */
export const MIME_TO_EXT = /* @__PURE__ */ (() => {
	const map: Record<string, string> = {};
	for (const [ext, mime] of Object.entries(EXT_MIME)) {
		const baseMime = mime.split(';')[0]?.trim() ?? mime;
		// First extension wins (jpg before jpeg)
		if (!(baseMime in map)) {
			map[baseMime] = ext;
		}
	}
	// Non-standard alias widely used by browsers/servers
	map['image/jpg'] = 'jpg';
	return map;
})() satisfies Record<string, string>;

export const PREVIEW_LIMITS = {
	GRID_PREVIEW: 4,
	MAX_PROPERTY_SEARCH_RESULTS: 200,
} as const;

/* NO CONTENT-PREVIEW CAP HERE: the bounded preview of an error / log body for diagnostic display is
 * `ERROR_BODY_PREVIEW_MAX_CHARS` (`const/observability-error-preview.ts`), whose declared surface covers HTTP error
 * bodies, subprocess stderr tails, eslint output tails and unrecognized-envelope dumps alike. A truncation cap for
 * error text here would be a second spelling of that decision, misfiled besides: this leaf owns FILE limits. */

export const MAX_PROPERTIES = 2000;

// MIME

/** The top-level type every image media type opens with (`image/png`, `image/jpeg`, ...): a file whose reported type starts
 *  with it is shown as a picture. The subtypes are {@link EXT_MIME}'s. */
export const IMAGE_MEDIA_TYPE_PREFIX = 'image/';

// FILES

/** RFC 8089 file-URL scheme prefix -- a LOCAL path the agent reported, never a fetchable image. Use with
 *  `String#startsWith`: the image agent inlines such a URL as a data URL and the upload filter refuses one. */
export const FILE_URL_PREFIX = 'file://' as const;
